import { useLayoutEffect, useRef } from 'react';
import { formatSiretSiren, getSiretSirenCaretPosition } from '../shared/country';

// Reformatting the SIRET shifts every character after the SIREN, which makes the browser drop the
// caret at the end of the field. Capture the caret in digits while the input is parsed, then put it
// back once the reformatted value is on screen.
const useSiretCaret = siretSiren => {
  const inputRef = useRef(null);
  const caretRef = useRef(null);

  // Deliberately without a dependency array: deleting the separator produces a render where
  // siretSiren is unchanged, and that render still has to put the formatted value back.
  useLayoutEffect(() => {
    const input = inputRef.current;
    const caret = caretRef.current;

    if (!input || caret === null) {
      return;
    }
    caretRef.current = null;

    // Deleting the separator itself leaves the digits unchanged, so redux-form does not rewrite the
    // input and the raw value would stay on screen: put the formatted value back ourselves.
    const formatted = formatSiretSiren(siretSiren);
    if (input.value !== formatted) {
      input.value = formatted;
    }
    input.setSelectionRange(caret, caret);
  });

  // Called with the raw field value while the DOM still holds the caret the user left behind.
  const captureCaret = rawValue => {
    const input = inputRef.current;

    if (!input) {
      return;
    }

    const digitsBeforeCaret = String(rawValue)
      .slice(0, input.selectionStart)
      .replace(/\D/g, '').length;

    caretRef.current = getSiretSirenCaretPosition(digitsBeforeCaret);
  };

  return { inputRef, captureCaret };
};

export default useSiretCaret;
